/** @format */

import React from "react";
import { useTranslation } from "react-i18next";
import { useLocale } from "../i18n/hooks";
import PageHeader from "../pageHeader/PageHeader";
import { ReactComponent as Printer } from "./../../assets/icons/print.svg";
import Activity from "../activityCards/Activity";
import ActivityAnswers from "../activityCards/ActivityAnswers";
import FormattedText from "../formattedText/FormattedText";
import { FullResource } from "../resources/ResourceFunctions";
import { Card } from "../resources/types";

const PrintableAppUI: React.FC<{ resource: FullResource }> = ({ resource }) => {
  const locale = useLocale();
  const { t: _ } = useTranslation();

  return (
    <>
      {/* Header message (Hidden on print) */}
      <aside className="d-print-none">
        <div className="alert alert-info mb-0 clearfix d-flex align-items-center justify-content-between rounded-0">
          <div>
            {" "}
            {locale === "en" && (
              <>
                <p className="mb-2">
                  You are viewing the print friendly version of this blended
                  learning resource. To print this resource select the print
                  button or you can use the shortcut <strong>Ctrl+P</strong> in
                  your browser.
                </p>
                <p className="m-0">
                  <a
                    className="alert-link"
                    href={window.location.href.replace("/print/", "/")}
                  >
                    To view the online version of this resource please click
                    here.
                  </a>
                </p>
              </>
            )}
            {locale === "cy" && (
              <>
                <p className="mb-2">
                  Rydych yn edrych ar y fersiwn argraffadwy o'r adnodd dysgu
                  cyfunol hwn. Er mwyn argraffu'r adnodd, dewiswch y botwm
                  argraffu neu gallwch ddefnyddio'r llwybr byr{" "}
                  <strong>Ctrl+P</strong> yn eich porwr.
                </p>
                <p className="m-0">
                  <a
                    className="alert-link"
                    href={window.location.href.replace("/print/", "/")}
                  >
                    I weld fersiwn ar-lein yr adnodd hwn cliciwch yma.
                  </a>
                </p>
              </>
            )}
          </div>
          <button
            className="btn btn-info ms-auto text-nowrap"
            onClick={() => {
              window.print();
            }}
          >
            <Printer className="mr-2" />
            {_("Print resource")}
          </button>
        </div>
      </aside>

      <div
        data-testid="print-wrapper"
        className="p-3 bg-white print-wrapper"
        onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          e.stopPropagation();
          e.preventDefault();
        }}
      >
        <hr />
        <PageHeader
          resourceTitle={resource.Object.resourceTitle[locale]}
          subject={resource.Subject}
          themeLevel={resource.Object.qualification || ""}
        />

        <hr />
        <div className="mt-3 mb-5" style={{ pageBreakBefore: "always" }}>
          <h4>{resource.Object.resourceTitle[locale]}</h4>
          <ol>
            {resource.Object.lessons.map((lesson, lessonIndex) => (
              <li key={lessonIndex}>
                {typeof lesson.title === "object" ? (
                  <FormattedText html={lesson.title?.[locale] ?? ""} />
                ) : (
                  <FormattedText html={lesson?.title} />
                )}
                <ul style={{ listStyle: "none" }}>
                  {lesson.subLessons.map((subLesson, subLessonIndex) => {
                    const subLessonTitle =
                      typeof subLesson.title === "object"
                        ? subLesson?.title[locale]
                        : subLesson?.title;

                    return (
                      <li key={subLessonIndex}>
                        <span className="fw-bold">
                          {lessonIndex + 1}.{subLessonIndex + 1}.
                        </span>{" "}
                        <FormattedText html={subLessonTitle ?? ""} />
                      </li>
                    );
                  })}
                </ul>
              </li>
            ))}
          </ol>
        </div>

        {/* Resources */}
        {resource.Object.lessons.map((lesson, lessonIndex) => (
          <div key={`lesson-${lessonIndex}`} className="pt-3">
            <h1>
              {lessonIndex + 1}.{" "}
              {typeof lesson.title === "object" ? (
                <FormattedText html={lesson.title?.[locale] ?? ""} />
              ) : (
                <FormattedText html={lesson?.title} />
              )}
            </h1>
            {lesson.subLessons.map((page, pageIndex) => {
              const subLessonTitle =
                typeof page.title === "object"
                  ? page?.title[locale]
                  : page?.title;

              return (
                <div key={`${lessonIndex}-${pageIndex}`}>
                  <h2 className="mt-2">
                    <FormattedText
                      html={`${lessonIndex + 1}.${
                        pageIndex + 1
                      }. ${subLessonTitle}`}
                    />
                  </h2>
                  {lesson.difficultyLevels?.map((level, index) => {
                    let label = "";
                    switch (level) {
                      case "foundation":
                        label = _("Foundation tier");
                        break;
                      case "intermediate":
                        label = _("Intermediate tier");
                        break;
                      case "higher":
                        label = _("Higher tier");
                        break;
                    }
                    return (
                      <span key={index} title={label}>
                        {`[ ${label} ] `}
                      </span>
                    );
                  })}
                  <div>
                    {page.cards.map((card: Card, cardIndex: number) => (
                      <div
                        key={`${lessonIndex}-${pageIndex}-${cardIndex}`}
                        className="activityCard mb-3"
                      >
                        <Activity
                          id={`${lessonIndex + 1}.${pageIndex + 1}.${
                            cardIndex + 1
                          }`}
                          card={card}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ))}

        <div className="mt-3 mb-5">
          <h1>{_("Answers")}</h1>
          <h2 className="fs-4">{resource.Object.resourceTitle[locale]}</h2>
          <div>
            {resource.Object.lessons.map((lesson, lessonIndex) => (
              <div key={`lesson-${lessonIndex}`} className="lesson">
                {lesson.subLessons.map((page, pageIndex) => (
                  <div
                    key={`${lessonIndex}-${pageIndex}`}
                    className="subLesson"
                  >
                    {page.cards.map((card: Card, cardIndex: number) => (
                      <div
                        key={`${lessonIndex}-${pageIndex}-${cardIndex}`}
                        className="activityCard mb-3"
                      >
                        <ActivityAnswers
                          id={`${lessonIndex + 1}.${pageIndex + 1}.${
                            cardIndex + 1
                          }`}
                          card={card}
                        />
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintableAppUI;
